<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { stripInlineBase64Images } from 'dashboard/helper/editorHelper';
import { useUISettings } from 'dashboard/composables/useUISettings';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter';

// Assinatura própria dos e-mails (texto formatado, links e imagens enviadas pelo editor).
// Fica fora do campo de resposta e entra no fim do e-mail no envio; vazia, o e-mail usa
// a assinatura de mensagens.
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const signature = ref(uiSettings.value?.email_signature || '');
const isSaving = ref(false);

watch(
  () => uiSettings.value?.email_signature,
  value => {
    if (!isSaving.value) signature.value = value || '';
  }
);

const save = async () => {
  const { sanitizedContent, hasInlineImages } = stripInlineBase64Images(
    signature.value || ''
  );
  signature.value = sanitizedContent.trim();
  if (hasInlineImages) {
    useAlert(t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_SECTION.INLINE_IMAGE'));
  }
  isSaving.value = true;
  try {
    await updateUISettings({ email_signature: signature.value });
    useAlert(t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_SECTION.SAVED'));
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_SECTION.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const previewHtml = () =>
  new MessageFormatter(signature.value).formattedMessage;
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="save">
    <WootMessageEditor
      id="email-signature-input"
      v-model="signature"
      class="message-editor h-[12rem] !px-3"
      is-format-mode
      :placeholder="
        t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_SECTION.PLACEHOLDER')
      "
      channel-type="Context::EmailSignature"
      :enable-suggestions="false"
    />
    <div
      v-if="signature.trim()"
      class="rounded-lg border border-n-weak bg-white p-4 text-sm leading-relaxed text-[#1f2937] [&_img]:h-auto [&_img]:max-w-full [&_a]:text-[#1d4ed8] [&_a]:underline"
      data-test="email-signature-preview"
    >
      <span class="mb-2 block text-xs font-semibold uppercase text-[#6b7280]">
        {{ t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_SECTION.PREVIEW') }}
      </span>
      <div v-dompurify-html="previewHtml()" />
    </div>
    <div>
      <NextButton
        type="submit"
        :is-loading="isSaving"
        :label="t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_SECTION.SAVE')"
      />
    </div>
  </form>
</template>
